import { AxiosInstance } from 'axios'

/**
 * Målindex – API-lager (speglar server/src/controllers/v1/MalindexController.ts).
 *
 * Ett BESÖK är enheten. Ett besök kan bära en behovsskattning, en nyttoskattning
 * eller båda. Indexet räknas ut och fryses på servern mot den senaste
 * behovsskattningen som fanns FÖRE besöket – frontend räknar bara för förhandsvisning.
 */

export interface SkalaAlternativ {
  varde: number
  etikett: string
}

export interface MalindexOmrade {
  id: number
  nyckel: string
  namn: string
  /** Behovsfrågan, ordagrant ur Harriets original. */
  fraga: string
  /** Nyttoformuleringen av samma område (spec v0.3 §5.2). */
  nyttofraga: string
  varfor?: string
  fordjupningsfragor: string[]
  malforslag: string[]
}

export interface SvarandeAlternativ {
  nyckel: string
  etikett: string
}

export interface MalindexConfig {
  version: number
  batteriversion: string
  beskrivning?: string
  /** Femgradig 0–4, samma skala för behov, nytta och måluppfyllelse. */
  skala: SkalaAlternativ[]
  /** Sentinel utanför skalan (9). Exkluderas ur både täljare och nämnare. */
  ejTillampligt: SkalaAlternativ
  behovLeadIn: string
  behovLeadInUppfoljning: string
  nyttoLeadIn: string
  maxPrioriterade: number
  maxMal: number
  uppstart: {
    /** Valfria i typen: aldre seedade configar saknar dem, koden har fallback. */
    kanalFraga?: string
    kanalAlternativ?: SvarandeAlternativ[]
    /** Vad samtalet handlar om, per besökstyp ('kartlaggning' | 'uppfoljning'). */
    samtalsbeskrivning?: Record<string, string>
    /**
     * Oanvända sedan samtyckesfrågan togs bort 2026-09-14. Står kvar i typen bara för
     * att configar som redan seedats bär dem – läs dem inte.
     */
    samtyckeFragor?: Record<string, string>
    samtyckeText?: string
    svarandeFraga: string
    svarandeAlternativ: SvarandeAlternativ[]
    demensFraga: string
    demensNotis?: string
  }
  mal: {
    mallText: string
    egnaOrdEtikett: string
    egnaOrdNotis?: string
    uppfyllelseFraga: string
    behallFraga: string
  }
  oppenFraga: string
  sammanfattning: {
    viktigtFran: number
    mindreBraDiff: number
    notis?: string
  }
  omraden: MalindexOmrade[]
}

/** Ett mål. Fristående – kopplas INTE till något område. */
export interface MalindexMal {
  malId: string
  /** Brukarens egna ord, ordagrant. Det är den formuleringen som läses upp. */
  egnaOrd: string
  maltext: string
}

/** Gradering av ett mål satt vid ett tidigare besök. */
export interface MalUppfoljning {
  malId: string
  uppfyllelse: number | null
  behall: boolean | null
}

/**
 * Vad handläggaren startade. Styr perioden: en kartläggning börjar om, en uppföljning
 * fortsätter. Se `pagaendePeriod` i malindexCalc.
 */
export type Besokstyp = 'kartlaggning' | 'uppfoljning'

export interface MalindexBesok {
  id: number
  personnummer: string
  process_id: string | null
  /** null på besök sparade innan kolumnen fanns – då härleds typen, se malindexCalc. */
  besokstyp: Besokstyp | null
  /** 'hembesok' | 'telefon'. null pa besok sparade innan faltet fanns. */
  kanal: string | null
  svarande: string | null
  demensdiagnos: boolean | null
  /**
   * null på besök sparade efter 2026-09-14, när samtyckesfrågan togs bort – "inte
   * tillfrågad". Äldre besök bär sitt dåvarande svar. Aldrig false som ersättning:
   * det hade sett ut som att brukaren sagt nej.
   */
  samtycke: boolean | null
  behov_svar: Record<string, number> | null
  nytta_svar: Record<string, number> | null
  /** Områden som stjärnmärktes vid besöket. Lagras för analys, visas inte i detaljvyn. */
  prioriterade: number[]
  mal: MalindexMal[]
  mal_uppfoljning: MalUppfoljning[]
  /** null = ej beräkningsbart (aldrig 0 som ersättning). */
  index_total: number | null
  /** Id på besöket vars behovsskattning viktade indexet. */
  index_viktat_av: number | null
  batteriversion: string | null
  config_id: number | null
  /**
   * Pausat besök. Ett utkast producerar INGENTING – inget index, ingen behovsbild som
   * viktar nästa mätning, inga mål, ingen periodgräns. Se `raknade` i malindexCalc.
   */
  draft: boolean
  /** Vilket steg i flödet utkastet stod på. null på ett färdigt besök. */
  utkast_steg: number | null
  note: string | null
  created: string
}

export interface SparaBesok {
  /** Satt när ett pausat besök sparas igen – raden uppdateras i stället för att dubbleras. */
  id?: number | null
  pnr: string
  pid?: string | null
  /** Steget utkastet stod på. Bara relevant tillsammans med draft. */
  utkastSteg?: number | null
  besokstyp?: Besokstyp | null
  kanal?: string | null
  svarande?: string | null
  demensdiagnos?: boolean | null
  behovSvar?: Record<string, number> | null
  nyttaSvar?: Record<string, number> | null
  prioriterade?: number[]
  mal?: MalindexMal[]
  malUppfoljning?: MalUppfoljning[]
  draft?: boolean
  note?: string
}

const tenant = () => localStorage.getItem('tenant_id')
const auth = () => ({ authorization: 'Bearer ' + localStorage.getItem('hydra_access_token') })

export const getMalindexConfig = async (api: AxiosInstance) => {
  const res = await api.get<MalindexConfig | null>(`v1/malindex/${tenant()}/config`, { headers: auth() })
  return res.data
}

/**
 * Hämtar den config-version ett tidigare besök använde, så en uppföljning ställer
 * exakt samma frågor som kartläggningen även om configen hunnit uppdateras.
 */
export const getMalindexConfigVersion = async (api: AxiosInstance, configId: number) => {
  const res = await api.get<MalindexConfig | null>(`v1/malindex/${tenant()}/config-version`, {
    headers: { ...auth(), configid: configId },
  })
  return res.data
}

export const getMalindexByBrukare = async (api: AxiosInstance, personnummer: string) => {
  const res = await api.get<MalindexBesok[]>(`v1/malindex/${tenant()}/brukare`, {
    headers: { ...auth(), ssn: personnummer },
  })
  return res.data
}

export const getMalindexById = async (api: AxiosInstance, id: number) => {
  const res = await api.get<MalindexBesok>(`v1/malindex/${tenant()}/${id}`, { headers: auth() })
  return res.data
}

export const saveMalindexBesok = async (api: AxiosInstance, payload: SparaBesok) => {
  const res = await api.post(`v1/malindex/${tenant()}/besok`, payload, { headers: auth() })
  return res.data as { id: number; indexTotal: number | null; indexViktatAv: number | null }
}

/** Kompletterar ÄrendeID på ett besök som sparats utan. Kolumnen heter process_id. */
export const setMalindexProcessId = async (api: AxiosInstance, id: number, processId: string) => {
  const res = await api.post(`v1/malindex/${tenant()}/besok/process-id`, { id, processId }, { headers: auth() })
  return res.data
}

export const archiveMalindexBesok = async (api: AxiosInstance, id: number) => {
  const res = await api.get(`v1/malindex/${tenant()}/archive`, { headers: { ...auth(), id } })
  return res.data
}

// Admin-editorn.
export const saveMalindexConfig = async (api: AxiosInstance, config: MalindexConfig, description: string) => {
  const res = await api.post(`v1/malindex/${tenant()}/config`, { config, description }, { headers: auth() })
  return res.data
}
